/**
 * Centralized UI labels and text constants.
 * Management can update labels here without a full rebuild of logic.
 */

export const APP_NAME = 'Documents Review';
export const APP_SHORT_NAME = 'Deposit Review';

// Release-candidate label baked into the bundle at build time via the
// vite.config `define` of __RC_VERSION__ (populated from VITE_RC_VERSION /
// RC_VERSION env). Displayed as `RC v.<version>` on the Login page +
// sidebar. Falls back to `dev` for local builds.
declare const __RC_VERSION__: string;
const RC_RAW = __RC_VERSION__ || 'dev';
export const RC_LABEL = `RC v.${RC_RAW}`;

export const NAV_LABELS = {
  dashboard: 'Dashboard',
  cases: 'Cases',
  newCase: 'New Case',
  packageDrop: 'Package Drop',
  tools: 'Tools',
  dataWash: 'Documents Review',
  admin: 'Settings',
  audit: 'Audit Log',
} as const;

export const CASE_STATUS_LABELS: Record<string, string> = {
  DRAFT: 'Draft',
  REVIEWING: 'Reviewing',
  SUMMARIZED: 'Summarized',
  INTAKE: 'Intake',
  PUBLIC_LOOKUP: 'Public Lookup',
  DOC_ANALYSIS: 'Document Analysis',
  SCORING: 'Scoring',
  SUMMARY: 'Summary',
  PENDING_DETERMINATION: 'Pending Determination',
  DETERMINED: 'Determined',
  CLOSED: 'Closed',
  ESCALATED: 'Escalated',
};

export const TIER_LABELS: Record<string, string> = {
  STANDARD: 'Standard',
  ENHANCED_REVIEW: 'Enhanced Review',
  ESCALATE: 'Escalate',
};

export const SEVERITY_LABELS: Record<string, string> = {
  AUTO_ESCALATE: 'Auto-Escalate',
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low',
};

export const ROLE_LABELS: Record<string, string> = {
  analyst: 'Intake Analyst',
  reviewer: 'Reviewer',
  principal: 'Principal',
  compliance: 'Compliance',
  operator: 'Operator',
};

export const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  cover_page: 'Cover Page',
  acquisition_doc: 'Acquisition Document',
  legal_opinion: 'Legal Opinion',
  transfer_agent: 'Transfer Agent',
  other: 'Other',
};

export const SUPPORTED_FORMATS = [
  'txt', 'csv', 'md', 'json', 'html', 'xml',
  'pdf', 'image (png/jpg)',
  'Word .docx', 'RTF', 'Excel .xlsx', 'zip',
];

export const UNSUPPORTED_FORMATS = ['.doc', '.xls', '.ppt'];

export const ADMIN_LABELS = {
  title: 'Administration',
  modelConfig: 'Model Configuration',
  rubric: 'Rubric',
  learnedRules: 'Learned Rules',
  rulesProfile: 'Rules Profile',
  dataSources: 'Data Sources',
  users: 'Users',
  egressLog: 'Compliance Audit',
  path: 'Path',
  profile: 'Profile',
  provider: 'Provider',
  modelName: 'Model Name',
  backendType: 'Backend',
  cloud: 'Cloud',
  local: 'Local',
  openai: 'vLLM (OpenAI)',
  anthropic: 'Hosted AI',
  claudeCode: 'AI Agent',
  piAgent: 'pi (agent)',
  apiKeyRef: 'API Key Env Var (name only, never the secret)',
  anthropicNote: 'Hosted AI is a cloud backend -- available on the PUBLIC path only; the private path stays local per the data-security policy.',
  agenticNote: 'Uses the hosted AI subscription (no API key). Calls go through the agentic-bridge service.',
  bridgeUrl: 'Bridge URL',
  bridgeUrlPlaceholder: 'http://agentic-bridge:8787',
  active: 'Active',
  inactive: 'Inactive',
  save: 'Save',
  saving: 'Saving...',
  saved: 'Saved',
  guardRailWarning: 'DATA SECURITY: private path cannot use a cloud model outside DEV',
  guardRailActive: 'Data security active',
  egressEnabled: 'Enabled',
  egressDisabled: 'Disabled',
  egressStatus: 'Egress Status',
  toggleEgress: 'Toggle egress',
  sourceUrl: 'Source URL',
  description: 'Description',
  createUser: 'Create User',
  editUser: 'Edit User',
  fullName: 'Full Name',
  email: 'Email',
  role: 'Role',
  status: 'Status',
  activeStatus: 'Active',
  inactiveStatus: 'Inactive',
  noConfigs: 'No model configurations found.',
  noSources: 'No data sources configured.',
  noUsers: 'No users found.',
  configSaved: 'Model configuration saved',
  egressUpdated: 'Egress status updated',
  userCreated: 'User created successfully',
  userUpdated: 'User updated successfully',
  guardRailError: 'Data security violation: cannot save this configuration',
  accessDenied: 'Access denied: operator role required',
  publicPath: 'Public',
  privatePath: 'Private',
  devProfile: 'Dev',
  uatProfile: 'UAT',
  prodProfile: 'Prod',
} as const;

export const AUTH_LABELS = {
  loginTitle: 'Sign in to Deposit Review',
  email: 'Email',
  password: 'Password',
  signIn: 'Sign In',
  signingIn: 'Signing in...',
  loginError: 'Invalid email or password',
} as const;

export const CASE_LABELS = {
  createNew: 'Create New Case',
  createManual: 'Manual',
  createAutomatic: 'Automatic',
  packageDrop: 'Package Drop Intake',
  caseNumber: 'Case #',
  status: 'Status',
  tier: 'Tier',
  issuer: 'Issuer',
  symbol: 'Symbol',
  depositor: 'Depositor',
  depositDate: 'Deposit Date',
  createdAt: 'Created',
  updatedAt: 'Updated',
  needsReview: 'Needs Manual Review',
  noCases: 'No cases yet',
  noCasesDesc: 'Create your first case to get started with the deposit review process.',
  createFirst: 'Create Your First Case',
  dropPackage: 'Drop a Package',
  searchPlaceholder: 'Search issuer or depositor...',
  allStatuses: 'All Statuses',
  allTiers: 'All Tiers',
  page: 'Page',
  of: 'of',
  prev: 'Previous',
  next: 'Next',
} as const;

export const MANUAL_CASE_LABELS = {
  title: 'Create Case (Manual)',
  subtitle: 'Enter case details and upload documents manually.',
  issuerName: 'Issuer Name',
  issuerNamePlaceholder: 'e.g. Acme Industries Inc.',
  ticker: 'Ticker Symbol',
  tickerPlaceholder: 'e.g. ACME',
  cusip: 'CUSIP',
  cusipPlaceholder: 'e.g. 123456789',
  depositorName: 'Depositor Name',
  depositorNamePlaceholder: 'e.g. John Doe or ABC Holdings LLC',
  depositorType: 'Depositor Type',
  depositorTypeOptions: ['Individual', 'Entity', 'Nominee/Custodial'],
  accountRef: 'Account Reference',
  accountRefPlaceholder: 'e.g. ACC-12345',
  depositDate: 'Deposit Date',
  shareQuantity: 'Share Quantity',
  shareQuantityPlaceholder: 'e.g. 50000',
  notes: 'Notes',
  notesPlaceholder: 'Additional notes about this case...',
  documents: 'Documents',
  dropFilesHere: 'Drop files here or click to browse',
  createCase: 'Create Case',
  creating: 'Creating...',
  caseCreated: 'Case created successfully',
  docUploadError: 'Case created but document upload failed',
} as const;

export const PACKAGE_DROP_LABELS = {
  title: 'Package Drop Intake',
  subtitle: 'Drop a package of documents and the system will auto-fill the case.',
  dropZone: 'Drop files or a zip archive here',
  dropZoneHint: 'or click to browse',
  processing: 'Processing documents...',
  uploadProgress: 'Uploading {n} file(s)...',
  intakeComplete: 'Intake complete',
  intakeError: 'Intake failed',
  autoFilled: 'Auto-Filled Fields',
  missingFields: 'Missing Fields (Manual Review Required)',
  extractedFacts: 'Extracted Facts',
  documents: 'Documents',
  docStatusOk: 'OK',
  docStatusErrored: 'Errored',
  docStatusNotVerifiable: 'Not Verifiable',
  confirmAndRun: 'Confirm & Run Review',
  confirmPullAndRun: 'Pull Public Data & Run Review',
  saveOnly: 'Save (Don\'t Run Review)',
  confirming: 'Confirming...',
  reviewStarted: 'Review started',
  caseSaved: 'Case saved',
  noFiles: 'No files uploaded yet',
  editFields: 'Review and edit auto-filled fields below before confirming.',
  manualReviewNeeded: 'Some fields could not be auto-identified. Please fill them in manually.',
  fileName: 'File Name',
  docType: 'Type',
  docStatus: 'Status',
  download: 'Download',
  originalDoc: 'Original Document',
} as const;

export const COMMON_LABELS = {
  loading: 'Loading...',
  error: 'Something went wrong',
  retry: 'Retry',
  cancel: 'Cancel',
  save: 'Save',
  delete: 'Delete',
  back: 'Back',
  search: 'Search',
  noResults: 'No results found',
} as const;